import React, { useState, useEffect } from 'react';
import { Star, Send, Copy, Check, Image, MessageSquare, Loader } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ContentForm = () => {
  const { currentUser } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    platform: '',
    contentType: '',
    topic: '',
    tone: '',
    keywords: '',
    additionalInfo: ''
  });

  // Tone options
  const toneOptions = [
    { id: 'professional', name: 'Professional' },
    { id: 'casual', name: 'Casual' },
    { id: 'humorous', name: 'Humorous' },
    { id: 'inspirational', name: 'Inspirational' },
    { id: 'educational', name: 'Educational' },
    { id: 'formal', name: 'Formal' }
  ];

  // Fetch platforms and content types on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use the new combined endpoint to get both platforms and content types in a single request
        const formOptions = await api.getFormOptions();
        
        setPlatforms(formOptions.platforms);
        setContentTypes(formOptions.contentTypes);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load platform and content type options. Please try again later.');
      }
    };
    
    fetchData();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      // Prepare keywords as array
      const keywords = formData.keywords
        .split(',')
        .map(keyword => keyword.trim())
        .filter(keyword => keyword.length > 0);
      
      const response = await api.generateContent({
        ...formData,
        keywords
      });
      
      setResult(response);
    } catch (error) {
      console.error('Error generating content:', error);
      setError('Our AI content generation service is currently experiencing issues. Please try again in a few minutes. If the problem persists, please contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Handle copy to clipboard
  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Get platform details
  const getPlatformDetails = (platformId) => {
    return platforms.find(p => p.id === platformId) || {};
  };

  // Get content type details
  const getContentTypeDetails = (contentTypeId) => {
    return contentTypes.find(ct => ct.id === contentTypeId) || {};
  };

  // Format the result content for display
  const formatContent = (content) => {
    if (!content) return '';
    
    // Split by sections (numbered lists, headers, etc.)
    return content.split('\n').map((line, index) => {
      // Check if line is a header
      if (line.startsWith('#') || /^\d+\./.test(line)) {
        return <p key={index} className="font-bold mt-3 mb-1">{line}</p>;
      }
      
      // Check if line contains hashtags
      if (line.includes('#')) {
        return (
          <p key={index} className="my-1">
            {line.split(' ').map((word, wordIndex) => (
              word.startsWith('#') 
                ? <span key={wordIndex} className="text-blue-500">{word} </span>
                : word + ' '
            ))}
          </p>
        );
      }
      
      // Regular line
      return line ? <p key={index} className="my-1">{line}</p> : <br key={index} />;
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <Star className="h-8 w-8 text-white mr-3" />
          <h1 className="text-2xl font-bold text-white">Social Media Content Generator</h1>
        </div>
        <p className="text-white opacity-90">
          Create engaging, platform-optimized social media content in seconds using AI. Perfect for marketers, influencers, and businesses looking to maintain an active social presence.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Form Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Generate Content</h2>
          
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {/* Platform Selection */}
            <div className="mb-4">
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1">
                Platform <span className="text-red-500">*</span>
              </label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select a platform</option>
                {platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Content Type Selection */}
            <div className="mb-4">
              <label htmlFor="contentType" className="block text-sm font-medium text-gray-700 mb-1">
                Content Type <span className="text-red-500">*</span>
              </label>
              <select
                id="contentType"
                name="contentType"
                value={formData.contentType}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select content type</option>
                {contentTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              
              {formData.contentType && (
                <p className="mt-1 text-sm text-gray-500">
                  {getContentTypeDetails(formData.contentType).description}
                </p>
              )}
            </div>
            
            {/* Topic Input */}
            <div className="mb-4">
              <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                Topic <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="topic"
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                required
                placeholder="What's your content about?"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* Tone Selection */}
            <div className="mb-4">
              <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-1">
                Tone
              </label>
              <select
                id="tone"
                name="tone"
                value={formData.tone}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select a tone (optional)</option>
                {toneOptions.map(tone => (
                  <option key={tone.id} value={tone.id}>
                    {tone.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Keywords Input */}
            <div className="mb-4">
              <label htmlFor="keywords" className="block text-sm font-medium text-gray-700 mb-1">
                Keywords (comma separated)
              </label>
              <input
                type="text"
                id="keywords"
                name="keywords"
                value={formData.keywords}
                onChange={handleChange}
                placeholder="product, sale, discount"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* Additional Info */}
            <div className="mb-6">
              <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                Additional Information
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows="3"
                placeholder="Any specific details or requirements for your content..."
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 px-4 rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Star className="h-5 w-5 mr-2" />
                  Generate Content
                </>
              )}
            </button>
          </form>
        </div>
        
        {/* Result Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Generated Content</h2>
            
            {result && (
              <button
                onClick={handleCopy}
                className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </button>
            )}
          </div>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader className="h-10 w-10 text-indigo-600 animate-spin mb-4" />
              <p className="text-gray-500">Generating your content...</p>
              <p className="text-sm text-gray-400 mt-2">This may take a few seconds</p>
            </div>
          ) : result ? (
            <div className="prose max-w-none">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                {formatContent(result.content)}
              </div>
              
              {/* Platform-specific tips */}
              {formData.platform && (
                <div className="mt-6 border-t pt-4">
                  <h3 className="text-md font-medium text-gray-700 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-2 text-indigo-600" />
                    Platform Tips: {getPlatformDetails(formData.platform).name}
                  </h3>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• Caption Length: {getPlatformDetails(formData.platform).captionLength}</li>
                    <li>• Hashtags: {getPlatformDetails(formData.platform).hashtagLimit}</li>
                    <li>• Best Practices: {getPlatformDetails(formData.platform).bestPractices}</li>
                  </ul>
                </div>
              )}
              
              {/* Visual content suggestions */}
              <div className="mt-6 border-t pt-4">
                <h3 className="text-md font-medium text-gray-700 flex items-center">
                  <Image className="h-4 w-4 mr-2 text-indigo-600" />
                  Visual Content Suggestions
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  For best results, pair your content with high-quality visuals that match your message.
                  Consider using stock photos, custom graphics, or branded images that align with your content.
                </p>
              </div>
              
              {/* AI source info */}
              <div className="mt-6 text-xs text-gray-400 text-right">
                {/* Generated using {result.source === 'deepseek' ? 'DeepSeek AI' : 'OpenAI'} */}
                Kanglei Tools Powered by KangleiCode
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <MessageSquare className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500">Fill out the form and click "Generate Content" to create your social media post</p>
              <p className="text-sm text-gray-400 mt-2">Your content will appear here</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Tips Section */}
      <div className="mt-8 bg-indigo-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-indigo-800 mb-3">Tips for Great Social Media Content</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-md shadow-sm">
            <h3 className="font-medium text-indigo-600 mb-2">Know Your Audience</h3>
            <p className="text-sm text-gray-600">Tailor your content to your specific audience's interests, pain points, and preferences.</p>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm">
            <h3 className="font-medium text-indigo-600 mb-2">Be Consistent</h3>
            <p className="text-sm text-gray-600">Maintain a consistent posting schedule and brand voice across all your content.</p>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm">
            <h3 className="font-medium text-indigo-600 mb-2">Engage & Respond</h3>
            <p className="text-sm text-gray-600">Don't just post—engage with your audience by responding to comments and messages.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentForm;
